import { useCallback, useEffect, useRef, ReactNode, useState } from "react"
import { useMap } from "@vis.gl/react-google-maps"

import { colors } from "@/theme"
import { prefersReducedMotion } from "@/utils/reducedMotion"

interface AnimatedEndpointMarkerProps {
  position: {
    lat: number
    lng: number
  }
  waveColor?: string
  children?: ReactNode
  markerContent?: "pickup" | "delivery"
  showWaves?: boolean
  onClick?: () => void
  /** Tooltip + accessible name of the marker (e.g. what a click does). Optional. */
  title?: string
  /**
   * PLA-267 approach choreography: when defined, the halo waves render with
   * intensity-scaled tempo/strength — 0 is the calm idle pulse (identical to
   * the legacy `showWaves` look), 1 is clearly more energetic but never
   * strobing. Defined values force the waves visible regardless of
   * `showWaves`; `undefined` falls back to legacy behavior. Under
   * prefers-reduced-motion the halo stays pinned at the idle look. Ignored by
   * the regular-marker fallback (no AdvancedMarkerElement DOM content).
   */
  pulseIntensity?: number
  /**
   * PLA-267 role swap: when true, runtime wave-visibility changes (the hub
   * halo handing its role over when the return leg starts) crossfade over
   * ~400ms instead of snapping. Reduced motion always swaps instantly.
   */
  animateWaveChanges?: boolean
}

// --- PLA-267 pulse tuning ----------------------------------------------------
// Idle values MUST equal the legacy wave look (2.5s cycle, 0.2 peak opacity)
// so pulseIntensity 0 renders exactly as before. Peak tempo stays >= 1.4s —
// an energetic breath, never a strobe.
const WAVE_IDLE_DURATION_S = 2.5
const WAVE_PEAK_DURATION_S = 1.4
const WAVE_IDLE_PEAK_OPACITY = 0.2
const WAVE_PEAK_PEAK_OPACITY = 0.45
/** Crossfade length for wave-visibility role swaps. */
const WAVE_TOGGLE_FADE_MS = 400

/** Returns true when lat/lng are finite numbers safe to pass to Google Maps. */
function areCoordsValid(lat: unknown, lng: unknown): boolean {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    !isNaN(lat as number) &&
    !isNaN(lng as number)
  )
}

export const AnimatedEndpointMarker = ({
  position,
  waveColor = colors.primary,
  children: _children,
  markerContent = "pickup",
  showWaves = true,
  onClick,
  title,
  pulseIntensity,
  animateWaveChanges = false,
}: AnimatedEndpointMarkerProps) => {
  const map = useMap()
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | google.maps.Marker | null>(
    null,
  )
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [useRegularMarker, setUseRegularMarker] = useState(false)
  const onClickRef = useRef(onClick)
  // Stable ref so the position-update effect can lazily create the marker when
  // coordinates transition from NaN → valid after the initial mount.
  const createMarkerRef = useRef<((pos: { lat: number; lng: number }) => void) | null>(null)

  // PLA-267: the wave halo is mutable AFTER marker creation (intensity scaling
  // + role-swap crossfades), so its DOM and the latest wave-related props live
  // in refs the stable applyWaveAppearance callback reads.
  const markerInnerRef = useRef<HTMLDivElement | null>(null)
  const wavesWrapRef = useRef<HTMLDivElement | null>(null)
  const waveElsRef = useRef<HTMLDivElement[]>([])
  const showWavesRef = useRef(showWaves)
  const pulseIntensityRef = useRef(pulseIntensity)
  const animateChangesRef = useRef(animateWaveChanges)
  const waveColorRef = useRef(waveColor)
  const markerContentRef = useRef(markerContent)

  /** Builds the (initially transparent) wave layer inside the marker content. */
  const createWaveLayer = useCallback((host: HTMLDivElement) => {
    const wrap = document.createElement("div")
    wrap.className = "pulse-wave-layer"
    wrap.style.position = "absolute"
    wrap.style.top = "0"
    wrap.style.left = "0"
    wrap.style.width = "100%"
    wrap.style.height = "100%"
    wrap.style.display = "flex"
    wrap.style.alignItems = "center"
    wrap.style.justifyContent = "center"
    wrap.style.pointerEvents = "none"
    wrap.style.opacity = "0"

    const waveSize = markerContentRef.current === "pickup" ? "32px" : "36px"
    const waves: HTMLDivElement[] = []
    for (let i = 1; i <= 3; i++) {
      const wave = document.createElement("div")
      wave.className = `pulse-wave wave-${i}`
      wave.style.position = "absolute"
      wave.style.width = waveSize
      wave.style.height = waveSize
      wave.style.border = `2px solid ${waveColorRef.current}`
      wave.style.borderRadius = "50%"
      wave.style.opacity = "0.2"
      waves.push(wave)
      wrap.appendChild(wave)
    }

    // Waves sit beneath the core content (core carries zIndex 10).
    host.insertBefore(wrap, host.firstChild)
    wavesWrapRef.current = wrap
    waveElsRef.current = waves
  }, [])

  /**
   * Applies the current wave props (visibility, intensity, crossfade flag) to
   * the live wave layer, creating it lazily on first need. Reduced motion pins
   * the halo at the idle look and disables the visibility crossfade — state
   * upstream still runs, only the flourish is dropped.
   */
  const applyWaveAppearance = useCallback(() => {
    const host = markerInnerRef.current
    if (!host) return

    const intensity = pulseIntensityRef.current
    const hasPulse = typeof intensity === "number"
    const visible = hasPulse || showWavesRef.current

    if (!wavesWrapRef.current) {
      if (!visible) return // never needed yet — keep the DOM lean
      createWaveLayer(host)
    }
    const wrap = wavesWrapRef.current
    if (!wrap) return

    const reduced = prefersReducedMotion()
    const level = hasPulse && !reduced ? Math.min(1, Math.max(0, intensity)) : 0
    const durationS = WAVE_IDLE_DURATION_S - level * (WAVE_IDLE_DURATION_S - WAVE_PEAK_DURATION_S)
    const peakOpacity =
      WAVE_IDLE_PEAK_OPACITY + level * (WAVE_PEAK_PEAK_OPACITY - WAVE_IDLE_PEAK_OPACITY)
    wrap.style.setProperty("--pulse-duration", `${durationS}s`)
    wrap.style.setProperty("--pulse-peak", `${peakOpacity}`)

    // Role-swap crossfade: visibility eases over ~400ms when allowed.
    wrap.style.transition =
      animateChangesRef.current && !reduced ? `opacity ${WAVE_TOGGLE_FADE_MS}ms ease` : "none"
    wrap.style.opacity = visible ? "1" : "0"

    waveElsRef.current.forEach((wave, index) => {
      // Keep border color in sync (waveColor no longer recreates the marker).
      wave.style.borderColor = waveColorRef.current
      if (hasPulse) {
        // Intensity mode: tempo rides the CSS variable (updating a variable
        // does NOT restart a running animation) and the negative delays keep
        // the three waves evenly phased at any tempo.
        wave.style.animation = `pulseWave var(--pulse-duration, 2.5s) ease-out infinite`
        wave.style.animationDelay = `calc(var(--pulse-duration, 2.5s) * -${index} / 3)`
      } else {
        // Legacy mode: exactly the pre-PLA-267 wave animation.
        wave.style.animation = `pulseWave 2.5s ease-out ${index * 0.8}s infinite`
      }
    })
  }, [createWaveLayer])

  useEffect(() => {
    if (!map) {
      createMarkerRef.current = null
      return
    }

    // Guard against NaN coordinates (WEB-1Q). Google Maps will throw a cryptic
    // "gmp-advanced-marker: lat: NaN" error if these values aren't valid numbers.
    // We still expose createMarker via ref so the position-update effect can call
    // it lazily once valid coordinates arrive (prevents marker never appearing when
    // the initial position payload has missing geo data).
    const createMarker = (pos: { lat: number; lng: number }) => {
      // Never create a marker with NaN coordinates (WEB-1Q).
      if (!areCoordsValid(pos.lat, pos.lng)) {
        return
      }

      // Check if AdvancedMarkerElement is available
      if (!google?.maps?.marker?.AdvancedMarkerElement) {
        setUseRegularMarker(true)

        // Create a regular marker with custom icon
        const iconSize = markerContent === "pickup" ? 32 : 36
        const marker = new google.maps.Marker({
          position: pos,
          map,
          icon: {
            url:
              markerContent === "pickup"
                ? `data:image/svg+xml,${encodeURIComponent(`
                  <svg width="${iconSize}" height="${iconSize}" viewBox="0 0 ${iconSize} ${iconSize}" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="${iconSize / 2}" cy="${iconSize / 2}" r="${iconSize / 2 - 1}" fill="${colors.primary}" opacity="0.2" />
                    <circle cx="${iconSize / 2}" cy="${iconSize / 2}" r="6" fill="${colors.surface}" />
                    <circle cx="${iconSize / 2}" cy="${iconSize / 2}" r="16" fill="none" stroke="${colors.primary}" stroke-width="10" />
                  </svg>
                `)}`
                : "https://143405217.fs1.hubspotusercontent-eu1.net/hubfs/143405217/HDP-CarAssist-Design.png",
            scaledSize: new google.maps.Size(iconSize, iconSize),
            anchor: new google.maps.Point(iconSize / 2, iconSize / 2),
          },
          zIndex: 50,
          title,
        })

        if (onClickRef.current) {
          marker.addListener("click", onClickRef.current)
        }

        markerRef.current = marker
        return
      }

      // Create a wrapper to ensure proper anchoring
      const wrapper = document.createElement("div")
      wrapper.style.position = "relative"
      wrapper.style.width = "1px"
      wrapper.style.height = "1px"

      // Create the marker content centered on the 1x1 wrapper
      const container = document.createElement("div")
      container.className = "animated-endpoint-marker"
      container.style.position = "absolute"
      container.style.width = "140px"
      container.style.height = "140px"
      container.style.top = "-70px"
      container.style.left = "-70px"
      container.style.display = "flex"
      container.style.alignItems = "center"
      container.style.justifyContent = "center"

      // PLA-267: wave halo is created/updated by applyWaveAppearance (below)
      // so intensity scaling and role-swap crossfades work without recreating
      // the marker. Legacy showWaves rendering is unchanged.
      markerInnerRef.current = container

      // Create the core marker content
      if (markerContent === "pickup") {
        // White center with red border for pickup
        const markerCore = document.createElement("div")
        markerCore.className = "marker-core"
        markerCore.style.position = "relative"
        markerCore.style.width = "12px"
        markerCore.style.height = "12px"
        markerCore.style.borderRadius = "50%"
        markerCore.style.border = `10px solid ${colors.primary}`
        markerCore.style.backgroundColor = colors.surface
        markerCore.style.boxShadow = "0 4px 8px rgba(17, 30, 44, 0.15)"
        markerCore.style.zIndex = "10"
        container.appendChild(markerCore)
      } else {
        // HDP logo for delivery
        const img = document.createElement("img")
        img.src =
          "https://143405217.fs1.hubspotusercontent-eu1.net/hubfs/143405217/HDP-CarAssist-Design.png"
        img.alt = "Inspection Hub"
        img.style.width = "36px"
        img.style.height = "36px"
        img.style.objectFit = "contain"
        img.style.filter = "drop-shadow(0 2px 4px rgba(0,0,0,0.2))"
        img.style.position = "relative"
        img.style.zIndex = "10"
        container.appendChild(img)
      }

      // Add container to wrapper
      wrapper.appendChild(container)

      // Add CSS animation if not already added
      if (!document.getElementById("animated-endpoint-styles")) {
        const style = document.createElement("style")
        style.id = "animated-endpoint-styles"
        // --pulse-peak defaults to the legacy 0.2 so markers without a
        // pulseIntensity prop render exactly as before (PLA-267).
        style.textContent = `
          @keyframes pulseWave {
            0% {
              transform: scale(1);
              opacity: var(--pulse-peak, 0.2);
              border-width: 2px;
            }
            100% {
              transform: scale(4);
              opacity: 0;
              border-width: 1px;
            }
          }

          .animated-endpoint-marker {
            pointer-events: auto;
            cursor: pointer;
          }

          .pulse-wave {
            pointer-events: none;
            transform-origin: center center;
          }
        `
        document.head.appendChild(style)
      }

      // Create the marker with the wrapper
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: pos,
        content: wrapper,
        zIndex: 50,
        title,
      })

      if (onClickRef.current) {
        marker.addListener("click", onClickRef.current)
      }

      markerRef.current = marker

      // Paint the wave halo for the current props (creates it lazily if needed).
      applyWaveAppearance()
    }

    // Expose via ref so the position-update effect can call it for lazy creation
    // when coordinates transition from NaN → valid after the initial mount.
    createMarkerRef.current = createMarker

    // Only create immediately if the initial coordinates are already valid.
    if (areCoordsValid(position.lat, position.lng)) {
      createMarker(position)
    }

    return () => {
      createMarkerRef.current = null
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
      markerInnerRef.current = null
      wavesWrapRef.current = null
      waveElsRef.current = []
      if (markerRef.current) {
        if ("setMap" in markerRef.current) {
          markerRef.current.setMap(null)
        } else {
          markerRef.current.map = null
        }
        markerRef.current = null
      }
    }
    // showWaves/waveColor/pulseIntensity are applied in place by the wave
    // effect below (PLA-267) — recreating the marker on those would snap the
    // role-swap crossfade.
  }, [map, markerContent, applyWaveAppearance, title])

  // PLA-267: keep the wave refs in sync with props and restyle the live wave
  // layer without recreating the marker (intensity scaling, role-swap fades).
  useEffect(() => {
    showWavesRef.current = showWaves
    pulseIntensityRef.current = pulseIntensity
    animateChangesRef.current = animateWaveChanges
    waveColorRef.current = waveColor
    markerContentRef.current = markerContent
    applyWaveAppearance()
  }, [showWaves, pulseIntensity, animateWaveChanges, waveColor, markerContent, applyWaveAppearance])

  // Update position without recreating marker.
  // Also handles lazy creation when the marker wasn't created at mount because
  // the initial coordinates were NaN (WEB-1Q) and valid coords arrive later.
  useEffect(() => {
    // Drop updates with invalid coordinates — never set NaN on a live marker.
    if (!areCoordsValid(position.lat, position.lng)) {
      return
    }

    // Lazy creation: if map is ready but marker doesn't exist yet, create it now.
    if (markerRef.current === null && createMarkerRef.current !== null) {
      createMarkerRef.current(position)
      // If creation still produced null (e.g. google.maps API not ready), bail out.
      if (markerRef.current === null) {
        return
      }
    }

    if (markerRef.current) {
      if (useRegularMarker && "setPosition" in markerRef.current) {
        // Regular marker
        ;(markerRef.current as google.maps.Marker).setPosition(position)
      } else if (!useRegularMarker && "position" in markerRef.current) {
        // Advanced marker
        ;(markerRef.current as google.maps.marker.AdvancedMarkerElement).position = position
      }
    }
  }, [position.lat, position.lng, useRegularMarker])

  // Update click handler without recreating marker
  useEffect(() => {
    onClickRef.current = onClick
  }, [onClick])

  return null
}
